import { RecrystaMark } from "./RecrystaLogo";

function fmtMoneyLocal(v, cur) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: cur || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${cur || ""} ${Math.round(v).toLocaleString()}`;
  }
}

/**
 * Render the structured Monday-briefing card.
 *
 * Props:
 *   briefing      — email_log doc (has briefing_json, sent_at, to_email, subject)
 *   cur           — currency ISO (defaults to USD)
 *   bookings_qtd  — number (optional). When absent we hide the Bookings-QTD stat.
 */
export default function BriefingCard({ briefing, cur, bookings_qtd }) {
  const b = briefing?.briefing_json || {};
  const h = b.headline || {};
  const hero = b.hero || null; // new server-computed source of truth
  const useHero = !!hero;

  // Delta chip
  const delta = useHero ? hero.delta_text : (h.delta || "");
  const deltaColor = delta.includes("↑")
    ? "bg-emerald-500"
    : delta.includes("↓")
    ? "bg-red-600"
    : "bg-slate-500";

  // Hero big number
  const heroBig = useHero ? (hero.projected_display || "—") : (h.forecast || "—");
  const heroLabel = useHero ? "Projected close (this quarter)" : "";
  const heroSublabel = useHero ? "Bookings QTD + weighted forecast of open deals" : "";

  // Support metrics (new layout: bookings / weighted / target / gap-to-target)
  const bookingsDisplay = useHero
    ? (hero.bookings_qtd_display || "—")
    : (bookings_qtd != null ? fmtMoneyLocal(bookings_qtd, cur) : null);
  const weightedDisplay = useHero ? (hero.weighted_display || "—") : null;
  const targetDisplay = useHero ? (hero.target_display || "—") : (h.target || "—");
  const gapDisplay = useHero ? (hero.gap_value_text || "—") : (h.gap || "—");
  const gapColor = useHero ? hero.gap_color : "#0f172a";

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      data-testid="briefing-card"
    >
      <div className="px-6 pt-5 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold inline-flex items-center gap-2">
          <RecrystaMark size={16} />
          Monday Briefing · Recrysta
        </div>
        <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 rounded-full font-bold">
          AI · Claude
        </span>
      </div>

      {/* Hero */}
      <div className="px-6 pt-2 pb-5">
        {heroLabel && (
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-1">
            {heroLabel}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="text-4xl font-extrabold tracking-tighter text-slate-900"
            data-testid="briefing-forecast"
          >
            {heroBig}
          </div>
          {delta && (
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-white text-xs font-bold ${deltaColor}`}
              data-testid="briefing-delta"
            >
              {delta}
            </span>
          )}
        </div>
        {heroSublabel && (
          <div className="mt-2 text-xs text-slate-500">{heroSublabel}</div>
        )}
        {useHero ? (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200 border-t border-b border-slate-200 py-3">
            <div className="px-3 first:pl-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Bookings QTD</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular" data-testid="briefing-bookings">{bookingsDisplay}</div>
            </div>
            <div className="px-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Weighted forecast</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular" data-testid="briefing-weighted">{weightedDisplay}</div>
            </div>
            <div className="px-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Target</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular" data-testid="briefing-target">{targetDisplay}</div>
            </div>
            <div className="px-3 last:pr-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Gap to target</div>
              <div className="mt-1 text-sm font-bold number-tabular" style={{ color: gapColor }} data-testid="briefing-gap">{gapDisplay}</div>
            </div>
          </div>
        ) : (
          // Backwards compat: old email_log docs before hero-block existed
          <div
            className={`mt-4 grid ${bookingsDisplay ? "grid-cols-3" : "grid-cols-2"} divide-x divide-slate-200 border-t border-b border-slate-200 py-3`}
          >
            <div className="pr-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Target</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular">{targetDisplay}</div>
            </div>
            <div className="px-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Gap</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular">{gapDisplay}</div>
            </div>
            {bookingsDisplay && (
              <div className="pl-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Bookings QTD</div>
                <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular">{bookingsDisplay}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Why it moved */}
      <div className="px-6 pb-4">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
          Why it moved
        </div>
        <div
          className="mt-2 text-sm text-slate-900 leading-relaxed"
          data-testid="briefing-delta-reason"
        >
          {h.delta_reason_sentence}
        </div>
        <div
          className="mt-2 text-sm text-slate-600 leading-relaxed"
          data-testid="briefing-changes-paragraph"
        >
          {b.changes_paragraph}
        </div>
      </div>

      {/* Action deals — two-column row, name left / amount right, guaranteed gap */}
      {(b.action_deals || []).length > 0 && (
        <div className="px-6 pb-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
            Deals needing action today
          </div>
          <div className="mt-2">
            {(b.action_deals || []).map((d, i) => (
              <div
                key={i}
                className={`py-3 ${i > 0 ? "border-t border-slate-200" : ""}`}
                data-testid={`briefing-action-${i}`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div
                    className="text-sm font-semibold text-slate-900 min-w-0 flex-1 break-words"
                    data-testid={`briefing-action-name-${i}`}
                  >
                    {d.deal_name}
                  </div>
                  <div
                    className="text-sm font-semibold text-slate-900 number-tabular whitespace-nowrap flex-shrink-0"
                    data-testid={`briefing-action-amount-${i}`}
                  >
                    {d.amount}
                    <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5 rounded-full">
                      {d.stage}
                    </span>
                  </div>
                </div>
                <div className="mt-1 text-sm text-slate-600">{d.reason_sentence}</div>
                <div className="mt-0.5 text-sm text-slate-900 font-medium">
                  → {d.action_sentence}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Funnel note */}
      {b.funnel_note && (
        <div
          className="mx-6 mb-4 rounded-lg bg-slate-100 px-4 py-3"
          data-testid="briefing-funnel-note"
        >
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold">
            Funnel note
          </div>
          <div className="mt-1 text-sm text-slate-700 leading-relaxed">{b.funnel_note}</div>
        </div>
      )}

      {/* Bright spot */}
      {b.bright_spot && (
        <div
          className="mx-6 mb-4 rounded-lg bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3"
          data-testid="briefing-bright-spot"
        >
          <div className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 font-bold">
            Bright spot
          </div>
          <div className="mt-1 text-sm text-emerald-900 leading-relaxed">{b.bright_spot}</div>
        </div>
      )}

      <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-500">
        Sent {new Date(briefing.sent_at).toLocaleString()} · to {briefing.to_email}
      </div>
    </section>
  );
}
